import styled from 'styled-components';
import { Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { client, Drug } from '../api';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { IframeContainer } from 'shared-utils/components/IframeContainer';
import { PageContainer } from 'design-system/src/components/PageContainer';
import { FormInfoResponse, useFormInfoForToken } from 'shared-utils/hooks/useFormInfoForToken';
import { FormNotFound } from 'shared-utils/components/forms/FormNotFound';
import { FormRevoked } from 'shared-utils/components/forms/FormRevoked';
import { FormFiller } from 'shared-utils/components/FormFiller/FormFiller';
import { Sidebar, SidebarTopBar, SIDEBAR_BORDER_COLOR } from 'design-system/src/components/Sidebar/Sidebar';
import { DrugPanel } from '../components/sidebar/SidebarPanels';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import { ErrorBoundary } from '@sentry/react';
import { FormTopBar } from 'shared-utils/components/forms/FormTopBar';
import { createFormFillerApi } from '../lib/formFillerApi';

// Note: This is very similar to `FormPage` in patient-app
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  width: 100%;
`;

const FormContentWrapper = styled.div<{ $hasSidebar: boolean }>`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  border-top: 1px solid ${SIDEBAR_BORDER_COLOR};

  @media (max-width: ${breakpoints.tabletMax}px) {
    // Hide sidebar on smaller screens
    ${Sidebar} {
      display: none;
    }
  }
`;

const FormMainContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0; // Prevents flex item from being larger than container
`;

const FormContentArea = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;

export function FormPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <PageContainer $background="extraLight" $navbarPlacement="top">
      <ContentContainer>
        <ErrorBoundary fallback={<PageLoadError />}>
          <Suspense fallback={<LoadingSpinner />}>
            <FormPageContent token={token} />
          </Suspense>
        </ErrorBoundary>
      </ContentContainer>
    </PageContainer>
  );
}

function FormPageContent({ token }: { token: string | null }) {
  const formInfo = useFormInfoForToken({ token, client });
  switch (formInfo.data?.kind) {
    case 'notFound':
      return (
        <>
          <FormTopBar />
          <FormNotFound />
        </>
      );
    case 'success':
      return <InnerFormPage formInfo={formInfo.data.info} token={token ?? ''} />;
    case 'revoked':
      return (
        <>
          <FormTopBar />
          <FormRevoked message={formInfo.data.message} />
        </>
      );
    default:
      return (
        <>
          <FormTopBar />
          <PageLoadError />
        </>
      );
  }
}

function SidebarFormContent({ drug }: { drug: Drug }) {
  return (
    <Sidebar>
      <SidebarTopBar />
      <DrugPanel drug={drug} />
    </Sidebar>
  );
}

function InnerFormPage({ formInfo, token }: { formInfo: FormInfoResponse; token: string }) {
  const drug = formInfo.drug;
  const showSidebar = !!drug;

  if (!showSidebar) {
    switch (formInfo.kind) {
      case 'pandadoc':
        return (
          <>
            <FormTopBar />
            <IframeContainer embeddedUrl={formInfo.embedded_form_url} />
          </>
        );
      case 'form_filler':
        // The top bar is rendered inside `FormFiller`
        return (
          <FormFiller
            token={token}
            sidebar={null}
            api={createFormFillerApi()}
            role="Prescriber"
            formName={formInfo.form_name ?? 'Tandem Form'}
            allowSignatureUpload={formInfo.allow_signature_upload}
          />
        );
      default: {
        const _typecheck: never = formInfo;
        return <PageLoadError />;
      }
    }
  }
  switch (formInfo.kind) {
    case 'pandadoc':
      return (
        <>
          <FormTopBar />
          <FormContentWrapper $hasSidebar>
            <FormMainContent>
              <FormContentArea>
                <IframeContainer embeddedUrl={formInfo.embedded_form_url} />
              </FormContentArea>
            </FormMainContent>
            <SidebarFormContent drug={drug} />
          </FormContentWrapper>
        </>
      );
    case 'form_filler':
      // The top bar is rendered inside `FormFiller`
      return (
        <FormFiller
          api={createFormFillerApi()}
          token={token}
          role="Prescriber"
          formName={formInfo.form_name ?? 'Tandem Form'}
          allowSignatureUpload={formInfo.allow_signature_upload}
          sidebar={<SidebarFormContent drug={drug} />}
          checkboxFeatures={
            formInfo.supports_provider_attestation && drug.brand_name
              ? { providerAttestedPatientConsent: { drugName: drug.brand_name } }
              : undefined
          }
        />
      );
    default: {
      const _typecheck: never = formInfo;
      return <PageLoadError />;
    }
  }
}
